import { useEffect, useState } from 'react';
import { useTokens } from './theme.jsx';
import {
  getAutomaticInstitutionLogoUrl,
  getInstitutionLogoUrl,
} from './services/institutions.js';

export function institutionInitials(name) {
  return String(name || 'School')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => { return part.charAt(0).toUpperCase(); })
    .join('') || 'SC';
}

export function effectiveInstitutionLogoSource(logoSource, logoFileId) {
  if (logoSource === 'AUTO' || logoSource === 'UPLOADED') { return logoSource; }
  return logoFileId ? 'UPLOADED' : 'AUTO';
}

/**
 * `onLogoError` fires when the logo image fails to load, so a parent (the logo
 * picker) can say the logo is broken instead of only showing initials.
 */
export default function InstitutionMark({
  name, slug, logoFileId, logoSource, autoLogoDomain, size = 44, showName = false,
  nameSize = 15, onLogoError,
}) {
  const t = useTokens();
  const activeSource = effectiveInstitutionLogoSource(logoSource, logoFileId);
  const logoUrl = activeSource === 'AUTO'
    ? getAutomaticInstitutionLogoUrl(autoLogoDomain)
    : getInstitutionLogoUrl(slug, logoFileId);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => { setLogoFailed(false); }, [logoUrl]);
  const showLogo = !!logoUrl && !logoFailed;

  return (
    <div className="flex items-center min-w-0 gap-3">
      {/* The tile every company and school logo uses (client LogoTile): a rounded
          square, 22% radius, the logo contained edge to edge on white, initials on
          cream when there is none. */}
      <div
        className="relative shrink-0 overflow-hidden flex items-center justify-center"
        style={{
          width: size,
          height: size,
          borderRadius: Math.max(3, Math.round(size * 0.22)),
          backgroundColor: showLogo ? '#FFFFFF' : '#F4F0E8',
          color: '#4A2C1D',
          fontFamily: t.fonts.display,
          fontWeight: 600,
          fontSize: Math.max(14, Math.round(size * 0.34)),
        }}
        aria-hidden="true"
      >
        {showLogo ? null : institutionInitials(name)}
        {showLogo ? (
          <img
            src={logoUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
            onError={() => { setLogoFailed(true); onLogoError?.(); }}
          />
        ) : null}
      </div>
      {showName ? (
        <div className="min-w-0">
          <div
            className="truncate leading-tight font-semibold"
            style={{ color: t.heading, fontFamily: t.fonts.body, fontSize: nameSize }}
          >
            {name || 'Your school'}
          </div>
          <div
            className="mt-1 truncate text-[13px]"
            style={{ color: t.muted, fontFamily: t.fonts.mono }}
          >
            Institution portal
          </div>
        </div>
      ) : null}
    </div>
  );
}
